import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { APP_ROLES } from "./invite.utils";
import { normalizeInviteEmail } from "./invite.utils";
import { db, updateDocument } from "./firestore.utils";

export const DEFAULT_TECH_COLOR = "#1976d2";

export const isScheduleRecordActive = (record) => record?.isActive !== false;

export const filterActiveScheduleRecords = (records) =>
  records.filter(isScheduleRecordActive);

const mapScheduleDoc = (docSnap) => ({
  id: docSnap.id,
  ...docSnap.data(),
});

const findScheduleRowByUserId = async (collectionName, userId) => {
  if (!userId) {
    return null;
  }

  const snapshot = await getDocs(
    query(collection(db, collectionName), where("userId", "==", userId)),
  );

  const docSnap = snapshot.docs[0];
  return docSnap ? mapScheduleDoc(docSnap) : null;
};

const findScheduleRowByEmail = async (collectionName, email) => {
  const normalizedEmail = normalizeInviteEmail(email);
  if (!normalizedEmail) {
    return null;
  }

  const snapshot = await getDocs(
    query(collection(db, collectionName), where("email", "==", normalizedEmail)),
  );

  const docSnap = snapshot.docs[0];
  return docSnap ? mapScheduleDoc(docSnap) : null;
};

const findLinkedScheduleRow = async (collectionName, { userId, email }) => {
  const byUserId = await findScheduleRowByUserId(collectionName, userId);
  if (byUserId) {
    return byUserId;
  }

  return findScheduleRowByEmail(collectionName, email);
};

const upsertTechnicianRow = async ({
  userId,
  email,
  displayName,
  isActive = true,
}) => {
  const existing = await findLinkedScheduleRow("technicians", { userId, email });
  const payload = {
    name: displayName.trim(),
    email: normalizeInviteEmail(email),
    userId: userId || existing?.userId || "",
    isActive,
    color: existing?.color || DEFAULT_TECH_COLOR,
  };

  if (existing) {
    await updateDocument(doc(db, "technicians", existing.id), payload);
    return existing.id;
  }

  const docRef = await addDoc(collection(db, "technicians"), payload);
  return docRef.id;
};

const upsertDispatcherRow = async ({
  userId,
  email,
  displayName,
  isActive = true,
}) => {
  const existing = await findLinkedScheduleRow("dispatchers", { userId, email });
  const payload = {
    name: displayName.trim(),
    email: normalizeInviteEmail(email),
    userId: userId || existing?.userId || "",
    isActive,
  };

  if (existing) {
    await updateDocument(doc(db, "dispatchers", existing.id), payload);
    return existing.id;
  }

  const docRef = await addDoc(collection(db, "dispatchers"), payload);
  return docRef.id;
};

const deactivateLinkedRow = async (collectionName, { userId, email }) => {
  const existing = await findLinkedScheduleRow(collectionName, { userId, email });
  if (existing) {
    await updateDocument(doc(db, collectionName, existing.id), { isActive: false });
  }
};

const updateLinkedRowNames = async ({ userId, email, displayName }) => {
  const technician = await findLinkedScheduleRow("technicians", { userId, email });
  if (technician) {
    await updateDocument(doc(db, "technicians", technician.id), {
      name: displayName.trim(),
    });
  }

  const dispatcher = await findLinkedScheduleRow("dispatchers", { userId, email });
  if (dispatcher) {
    await updateDocument(doc(db, "dispatchers", dispatcher.id), {
      name: displayName.trim(),
    });
  }
};

export const syncScheduleForUserRole = async ({
  role,
  userId = "",
  email,
  displayName,
  isActive = true,
}) => {
  if (!isActive) {
    await deactivateLinkedRow("technicians", { userId, email });
    await deactivateLinkedRow("dispatchers", { userId, email });
    return;
  }

  if (role === APP_ROLES.TECHNICIAN) {
    await upsertTechnicianRow({ userId, email, displayName, isActive: true });
    await deactivateLinkedRow("dispatchers", { userId, email });
    return;
  }

  if (role === APP_ROLES.DISPATCHER) {
    await upsertDispatcherRow({ userId, email, displayName, isActive: true });
    await deactivateLinkedRow("technicians", { userId, email });
    return;
  }

  if (role === APP_ROLES.ADMINISTRATION) {
    await deactivateLinkedRow("technicians", { userId, email });
    await deactivateLinkedRow("dispatchers", { userId, email });
  }
};

export const syncScheduleForInvite = async ({
  role,
  email,
  displayName,
  isActive = true,
}) =>
  syncScheduleForUserRole({
    role,
    userId: "",
    email,
    displayName,
    isActive,
  });

export const syncScheduleDisplayName = async ({ userId, email, displayName }) =>
  updateLinkedRowNames({ userId, email, displayName });
